import { ViewTypes } from './const'

/**
 * Centralised view capabilities configuration.
 *
 * Instead of scattering `viewType === ViewTypes.X` conditionals throughout the
 * codebase, components query this config to decide what to show/enable.
 *
 * To add support for a new view type, add a new entry here — no need to update
 * dozens of components.
 */

/**
 * Sentinel value for a toolbar capability that is enabled only for admin users.
 * Use this in place of the raw `'admin'` literal so the intent is searchable
 * and there's no string-typo risk at call sites.
 */
export const ADMIN_ONLY = 'admin' as const
export type AdminOnly = typeof ADMIN_ONLY

export type ToolbarCapability = boolean | AdminOnly

export interface ViewCapabilities {
  // Whether personal views can be made from this view (in a document). Form and Custom Views are owner-built
  // and shared by everyone, so they have none.
  personalViews: boolean

  // Column menu options
  columnMenu: {
    editColumn: boolean
    insertColumn: boolean
    duplicateColumn: boolean
    deleteColumn: boolean
    lockColumn: boolean
    hideColumn: boolean
    downloadAttachments: boolean
    filter: boolean
    colour: boolean
    group: boolean
    sort: boolean
    requiredField: boolean
    formWidth: boolean
  }

  // Toolbar buttons — true = all users, ADMIN_ONLY = admin only, false = disabled
  toolbar: {
    filter: ToolbarCapability
    sort: ToolbarCapability
    group: ToolbarCapability
    colour: ToolbarCapability
    hide: ToolbarCapability
    lock: ToolbarCapability
    freeze: ToolbarCapability
    importData: ToolbarCapability
    exportData: ToolbarCapability
    // Lookup Data From Other Tables
    joins: ToolbarCapability
    rowHeight: ToolbarCapability
    columnOrder: ToolbarCapability
    chartSettings: ToolbarCapability
    pivotSettings: ToolbarCapability
    matrixSettings: ToolbarCapability
    commentFilter: boolean
  }

  // Header display
  header: {
    showColumnTypeIcon: boolean
    resizeColumn: boolean
  }

  // Row operations
  rows: {
    addRow: boolean
    deleteRow: boolean
    expandRow: boolean
    duplicateRow: boolean
    insertRowAbove: boolean
    addColumn: boolean
    fillHandle: boolean
    aggregate: boolean
  }
}

const SPREADSHEET_CAPABILITIES: ViewCapabilities = {
  personalViews: true,
  columnMenu: {
    editColumn: true,
    insertColumn: true,
    duplicateColumn: true,
    deleteColumn: true,
    lockColumn: true,
    hideColumn: true,
    downloadAttachments: true,
    filter: true,
    colour: true,
    group: true,
    sort: true,
    requiredField: false,
    formWidth: false
  },
  toolbar: {
    filter: true,
    sort: true,
    group: true,
    colour: true,
    hide: true,
    lock: true,
    freeze: true,
    importData: true,
    exportData: true,
    joins: ADMIN_ONLY,
    rowHeight: true,
    columnOrder: true,
    chartSettings: false,
    pivotSettings: false,
    matrixSettings: false,
    commentFilter: true
  },
  header: {
    showColumnTypeIcon: true,
    resizeColumn: true
  },
  rows: {
    addRow: true,
    deleteRow: true,
    expandRow: true,
    duplicateRow: true,
    insertRowAbove: true,
    addColumn: true,
    fillHandle: true,
    aggregate: true
  }
}

const FORM_CAPABILITIES: ViewCapabilities = {
  personalViews: false,
  columnMenu: {
    editColumn: true,
    insertColumn: true,
    duplicateColumn: true,
    deleteColumn: true,
    lockColumn: true,
    hideColumn: true,
    downloadAttachments: true,
    filter: false,
    colour: false,
    group: false,
    sort: false,
    requiredField: true,
    formWidth: true
  },
  toolbar: {
    filter: false,
    sort: false,
    group: false,
    colour: false,
    hide: ADMIN_ONLY,
    lock: true,
    freeze: false,
    importData: false,
    exportData: false,
    joins: ADMIN_ONLY,
    rowHeight: true,
    columnOrder: true,
    chartSettings: false,
    pivotSettings: false,
    matrixSettings: false,
    commentFilter: true
  },
  header: {
    showColumnTypeIcon: true,
    resizeColumn: true
  },
  rows: {
    addRow: true,
    deleteRow: true,
    expandRow: true,
    duplicateRow: true,
    insertRowAbove: true,
    addColumn: true,
    fillHandle: false,
    aggregate: false
  }
}

const CHART_CAPABILITIES: ViewCapabilities = {
  personalViews: true,
  columnMenu: {
    editColumn: true,
    insertColumn: true,
    duplicateColumn: true,
    deleteColumn: true,
    lockColumn: false,
    hideColumn: false,
    downloadAttachments: false,
    filter: true,
    colour: true,
    group: false,
    sort: true,
    requiredField: false,
    formWidth: false
  },
  toolbar: {
    filter: true,
    sort: true,
    group: false,
    colour: true,
    hide: ADMIN_ONLY,
    lock: false,
    freeze: false,
    importData: false,
    exportData: false,
    joins: ADMIN_ONLY,
    rowHeight: false,
    columnOrder: false,
    chartSettings: true,
    pivotSettings: false,
    matrixSettings: false,
    commentFilter: true
  },
  header: {
    showColumnTypeIcon: true,
    resizeColumn: true
  },
  rows: {
    addRow: false,
    deleteRow: false,
    expandRow: false,
    duplicateRow: false,
    insertRowAbove: false,
    addColumn: false,
    fillHandle: false,
    aggregate: false
  }
}

const TRANSPOSE_CAPABILITIES: ViewCapabilities = {
  personalViews: true,
  columnMenu: {
    editColumn: false,
    insertColumn: false,
    duplicateColumn: false,
    deleteColumn: false,
    lockColumn: false,
    hideColumn: false,
    downloadAttachments: false,
    filter: false,
    colour: false,
    group: false,
    sort: false,
    requiredField: false,
    formWidth: false
  },
  toolbar: {
    filter: true,
    sort: false,
    group: false,
    colour: true,
    hide: true,
    lock: true,
    freeze: true,
    importData: false,
    exportData: false,
    joins: ADMIN_ONLY,
    rowHeight: true,
    columnOrder: true,
    chartSettings: false,
    pivotSettings: true,
    matrixSettings: false,
    commentFilter: false
  },
  header: {
    showColumnTypeIcon: false,
    resizeColumn: true
  },
  rows: {
    addRow: false,
    deleteRow: false,
    expandRow: false,
    duplicateRow: false,
    insertRowAbove: false,
    addColumn: false,
    fillHandle: false,
    aggregate: false
  }
}

const CALENDAR_CAPABILITIES: ViewCapabilities = {
  personalViews: true,
  columnMenu: {
    editColumn: false,
    insertColumn: false,
    duplicateColumn: false,
    deleteColumn: false,
    lockColumn: false,
    hideColumn: false,
    downloadAttachments: false,
    filter: true,
    colour: true,
    group: false,
    sort: false,
    requiredField: false,
    formWidth: false
  },
  toolbar: {
    filter: true,
    sort: false,
    group: false,
    colour: true,
    hide: ADMIN_ONLY,
    lock: false,
    freeze: false,
    importData: false,
    exportData: false,
    joins: ADMIN_ONLY,
    rowHeight: false,
    columnOrder: false,
    chartSettings: true,
    pivotSettings: false,
    matrixSettings: false,
    commentFilter: false
  },
  header: {
    showColumnTypeIcon: false,
    resizeColumn: false
  },
  rows: {
    addRow: false,
    deleteRow: false,
    expandRow: true,
    duplicateRow: false,
    insertRowAbove: false,
    addColumn: false,
    fillHandle: false,
    aggregate: false
  }
}

const MATRIX_CAPABILITIES: ViewCapabilities = {
  personalViews: true,
  columnMenu: {
    editColumn: false,
    insertColumn: false,
    duplicateColumn: false,
    deleteColumn: false,
    lockColumn: false,
    hideColumn: false,
    downloadAttachments: false,
    filter: false,
    colour: false,
    group: false,
    sort: false,
    requiredField: false,
    formWidth: false
  },
  toolbar: {
    filter: true,
    sort: false,
    group: false,
    colour: false,
    hide: false,
    lock: true,
    freeze: true,
    importData: false,
    exportData: false,
    joins: ADMIN_ONLY,
    rowHeight: true,
    columnOrder: false,
    chartSettings: false,
    pivotSettings: false,
    matrixSettings: true,
    commentFilter: false
  },
  header: {
    showColumnTypeIcon: true,
    resizeColumn: true
  },
  rows: {
    addRow: false,
    deleteRow: false,
    expandRow: false,
    duplicateRow: false,
    insertRowAbove: false,
    addColumn: false,
    fillHandle: false,
    aggregate: false
  }
}

const CUSTOM_VIEW_CAPABILITIES: ViewCapabilities = {
  personalViews: false,
  columnMenu: Object.fromEntries(
    Object.keys(CHART_CAPABILITIES.columnMenu).map((key) => [key, false])
  ) as ViewCapabilities['columnMenu'],
  toolbar: {
    ...(Object.fromEntries(
      Object.keys(CHART_CAPABILITIES.toolbar).map((key) => [key, false])
    ) as ViewCapabilities['toolbar']),
    // Authors choose which columns and rows the page can read, and the order rows reach it in.
    filter: true,
    sort: true,
    hide: ADMIN_ONLY
  },
  header: { showColumnTypeIcon: false, resizeColumn: false },
  rows: { ...CHART_CAPABILITIES.rows, expandRow: false }
}

const VIEW_CAPABILITIES: Record<number, ViewCapabilities> = {
  [ViewTypes.CUSTOM_VIEW]: CUSTOM_VIEW_CAPABILITIES,
  [ViewTypes.SPREADSHEET]: SPREADSHEET_CAPABILITIES,
  [ViewTypes.FORM]: FORM_CAPABILITIES,
  [ViewTypes.CHART]: CHART_CAPABILITIES,
  [ViewTypes.TRANSPOSE]: TRANSPOSE_CAPABILITIES,
  [ViewTypes.CALENDAR]: CALENDAR_CAPABILITIES,
  [ViewTypes.MATRIX]: MATRIX_CAPABILITIES
}

/** Get the capabilities for a given view type. Falls back to spreadsheet defaults. */
export const getViewCapabilities = (viewType: number): ViewCapabilities =>
  VIEW_CAPABILITIES[viewType] || SPREADSHEET_CAPABILITIES

/** Check if a capability is enabled for the current user. Handles ADMIN_ONLY values. */
export const isCapabilityEnabled = (value: ToolbarCapability, isAdmin: boolean): boolean =>
  value === true || (value === ADMIN_ONLY && isAdmin)

/** Check if any column menu option is enabled for a given view type. */
export const hasColumnMenuOptions = (viewType: number): boolean => {
  const capabilities = getViewCapabilities(viewType).columnMenu
  return Object.values(capabilities).some(Boolean)
}
